/**
 * 鉴权上下文 · 全局可访问当前登录用户
 * - 启动时从 localStorage 恢复
 * - login / logout 暴露给页面
 * - 监听 poet:unauthorized 事件（来自 http 拦截器），自动清空并跳登录
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import * as authApi from '@/api/auth';
import {
  clearAuth,
  getAccessToken,
  getStoredUser,
  setAuth as persistAuth,
  type StoredUser,
} from './storage';

interface AuthContextValue {
  user: StoredUser | null;
  isAuthenticated: boolean;
  login: (account: string, password: string, accountType: 'phone' | 'studentId' | 'employeeId') => Promise<{
    mustChangePassword: boolean;
    user: StoredUser;
  }>;
  logout: () => Promise<void>;
  setUserLocal: (user: StoredUser) => void;
}

const Ctx = createContext<AuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StoredUser | null>(() => {
    if (!getAccessToken()) return null;
    return getStoredUser();
  });

  // 监听 http 拦截器派发的未授权事件
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener('poet:unauthorized', handler);
    return () => window.removeEventListener('poet:unauthorized', handler);
  }, []);

  const login = useCallback(
    async (account: string, password: string, accountType: 'phone' | 'studentId' | 'employeeId') => {
      const res = await authApi.login({ account, password, accountType });
      persistAuth({
        accessToken: res.tokens.accessToken,
        refreshToken: res.tokens.refreshToken,
        user: res.user,
      });
      setUser(res.user);
      return { mustChangePassword: res.mustChangePassword, user: res.user };
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // 即使后端登出失败也本地清空
    }
    clearAuth();
    setUser(null);
  }, []);

  const setUserLocal = useCallback((u: StoredUser) => {
    setUser(u);
    const raw = localStorage.getItem('poet.user');
    if (raw) {
      localStorage.setItem('poet.user', JSON.stringify(u));
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: !!user, login, logout, setUserLocal }),
    [user, login, logout, setUserLocal],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
